import React from "react";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { AddRounded, DeleteOutlineRounded } from "@mui/icons-material";
import { SimpleSnackbar } from "../../../components/snackbar";
import { AdmLayout } from "../../../layouts/base/adm.layout";
import { MessageDialog } from "../../../components/message-dialog";
import {
    AdminFilterBar,
    AdminPageHeader,
    AppModal,
    countLabel,
    DataTable,
    EmptyState,
} from "../../../components/ui";
import { DcoForm } from "./form/dco-form";
import { DcoModel } from "./table/dco.model";
import { useDcoPage } from "./use-dco-page";
import { buildDcoColumns } from "./dco-columns";

export const DcoPage: React.FC = () => {
    const {
        loading,
        searchValue,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        showDeleteDialog,
        setShowDeleteDialog,
        list,
        tags,
        removeTag,
        setRemoveTag,
        tagDialog,
        setTagDialog,
        modalOpen,
        setModalOpen,
        formRef,
        onHandleEmailAdd,
        onDeleteEmailsByTag,
        handlePageChange,
        handlePageSizeChange,
        onDeleteEmail,
        handleDeleteEmail,
        handleSearch,
        displayedRows,
    } = useDcoPage();

    const [formValid, setFormValid] = React.useState(false);

    const columns = buildDcoColumns({ onDelete: handleDeleteEmail });

    return (
        <AdmLayout hasLogo={true} loading={loading}>
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <MessageDialog
                open={showDeleteDialog}
                message={"Tem certeza que deseja remover o E-mail?"}
                onConfirm={onDeleteEmail}
                confirmButtonText={"Remover"}
                cancelButtonText={"Cancelar"}
                onClose={() => {
                    setRemoveTag("");
                    setShowDeleteDialog(false);
                }}
            />

            <MessageDialog
                open={tagDialog.show}
                message={"Tem certeza que deseja remover os emails dessa Tag?"}
                onConfirm={onDeleteEmailsByTag}
                confirmButtonText={"Deletar"}
                cancelButtonText={"Cancelar"}
                onClose={() => setTagDialog({ show: false, tag: "" })}
            />

            <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
                <AdminPageHeader
                    title="DCO"
                    subtitle="E-mails DCO"
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddRounded />}
                            onClick={() => setModalOpen(true)}
                            sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        >
                            Liberar e-mails
                        </Button>
                    }
                />

                <AdminFilterBar
                    search={searchValue}
                    onSearch={handleSearch}
                    placeholder="Buscar por e-mail..."
                    filters={
                        <>
                            <Select
                                size="small"
                                value={removeTag}
                                onChange={(event) => setRemoveTag(event.target.value)}
                                displayEmpty
                                sx={{ height: 40, minWidth: 160, borderRadius: "10px" }}
                            >
                                <MenuItem value="">Remover por tag</MenuItem>
                                {tags.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteOutlineRounded />}
                                disabled={removeTag.length === 0}
                                onClick={() => setTagDialog({ show: true, tag: removeTag })}
                                sx={{ height: 40, borderRadius: "10px", whiteSpace: "nowrap" }}
                            >
                                Remover tag
                            </Button>
                        </>
                    }
                    count={countLabel(displayedRows.length, list.pagination.total)}
                />

                <DataTable<DcoModel>
                    columns={columns}
                    rows={displayedRows}
                    rowKey={(row) => String(row.id)}
                    countLabel={countLabel(displayedRows.length, list.pagination.total)}
                    loading={loading}
                    page={list.pagination.currentPage}
                    pageSize={list.pagination.perPage}
                    totalRows={list.pagination.total}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[5, 10, 25]}
                    minWidth={600}
                    emptyState={
                        <EmptyState
                            title="Nenhum e-mail encontrado"
                            description="Libere e-mails para exibição de planos exclusivos."
                            actionLabel="Liberar e-mails"
                            onAction={() => setModalOpen(true)}
                        />
                    }
                />
            </Box>

            <AppModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                kicker="Cadastrar"
                title="Liberar e-mails"
                maxWidth={600}
                footer={
                    <>
                        <Button variant="outlined" onClick={() => setModalOpen(false)} sx={{ height: 40, borderRadius: "10px" }}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => formRef.current?.submit()}
                            disabled={!formValid}
                            sx={{ height: 40, borderRadius: "10px" }}
                        >
                            Salvar
                        </Button>
                    </>
                }
            >
                <DcoForm
                    ref={formRef}
                    tags={tags}
                    onAdded={onHandleEmailAdd}
                    onValidityChange={setFormValid}
                    onDeleteTag={(tag) => setTagDialog({ show: true, tag: tag })}
                />
            </AppModal>
        </AdmLayout>
    );
};
